import { useState, useEffect, useCallback } from 'react';
import { fetchSymbols, fetchHistoricalIV } from '../utils/api';
import ScripSelector from './ScripSelector';
import IVAnalysis from './IVAnalysis';
import HistoricalChart from './HistoricalChart';
import { Container } from './ui';

export default function IVCalculator({ defaultSymbol = '' }) {
  const [symbols, setSymbols] = useState(null);
  const [selectedSymbol, setSelectedSymbol] = useState(defaultSymbol);
  const [selectedDate, setSelectedDate] = useState('');
  const [historicalData, setHistoricalData] = useState(null);
  const [currentIV, setCurrentIV] = useState(null);
  const [loadingSymbols, setLoadingSymbols] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Fetch symbols once on mount; set default date to last trading day
  useEffect(() => {
    fetchSymbols()
      .then((data) => {
        setSymbols(data);
        if (data.last_trading_date) {
          setSelectedDate(data.last_trading_date);
        }
      })
      .catch(() => setApiError('Failed to load symbols'))
      .finally(() => setLoadingSymbols(false));
  }, []);

  // Fetch historical data when symbol changes
  useEffect(() => {
    if (!selectedSymbol) {
      setHistoricalData(null);
      setCurrentIV(null);
      return;
    }
    setLoadingHistory(true);
    setApiError(null);

    fetchHistoricalIV(selectedSymbol)
      .then(setHistoricalData)
      .catch(() => setApiError(`Failed to load data for ${selectedSymbol}`))
      .finally(() => setLoadingHistory(false));
  }, [selectedSymbol]);

  const handleSymbolChange = useCallback((sym) => {
    setSelectedSymbol(sym);
  }, []);

  const handleDateChange = useCallback((e) => {
    setSelectedDate(e.target.value);
  }, []);

  const handleAnalysisLoaded = useCallback((ivPercent) => {
    setCurrentIV(ivPercent);
  }, []);

  return (
    <Container className="py-6 space-y-6">
      {apiError && (
        <div className="rounded-clay-sm bg-danger-50/80 dark:bg-danger-900/20 border border-danger-200/60 dark:border-danger-800/60 p-3 text-sm text-danger-700 dark:text-danger-400 shadow-clay dark:shadow-clay-dark">
          {apiError}
        </div>
      )}

      {/* Row 1: Controls */}
      <div className="bg-surface rounded-clay p-4 shadow-clay dark:shadow-clay-dark">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
          <div>
            <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              max={symbols?.last_trading_date || new Date().toISOString().split('T')[0]}
              className="input-base focus-ring"
            />
          </div>
          <ScripSelector
            symbols={symbols}
            selectedSymbol={selectedSymbol}
            onSymbolChange={handleSymbolChange}
            loading={loadingSymbols}
          />
        </div>
      </div>

      {/* Row 2: IV Analysis */}
      <div className="bg-surface rounded-clay p-5 shadow-clay dark:shadow-clay-dark">
        <IVAnalysis
          selectedSymbol={selectedSymbol}
          selectedDate={selectedDate}
          onAnalysisLoaded={handleAnalysisLoaded}
        />
      </div>

      {/* Row 3: Chart */}
      <div className="bg-surface rounded-clay p-5 shadow-clay dark:shadow-clay-dark">
        {loadingHistory ? (
          <div className="animate-pulse h-80 bg-neutral-100 dark:bg-white/5 rounded-clay-sm" />
        ) : (
          <HistoricalChart
            data={historicalData?.data}
            currentIV={currentIV}
            stats={historicalData?.stats}
            selectedDate={selectedDate}
          />
        )}
      </div>
    </Container>
  );
}
